import { useState } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { applyMask } from "shared/utils/masks";
import {
  CATEGORY_COLORS,
  CATEGORY_DESCRIPTIONS,
  ContractualRentCategory,
  RentCategoryKey,
} from "./contractualRent.types";

interface ContractualRentBreakdownProps {
  categories: ContractualRentCategory[];
  total: number;
}

const percentOf = (amount: number, total: number) =>
  total > 0 ? (amount / total) * 100 : 0;

/**
 * Stacked bar of the five categories plus an expandable list that splits each
 * category into its ledgers / attribute types.
 */
export const ContractualRentBreakdown = ({
  categories,
  total,
}: ContractualRentBreakdownProps) => {
  const [expanded, setExpanded] = useState<RentCategoryKey | null>(null);
  const visibleSegments = categories.filter((category) => category.amount > 0);

  return (
    <div className="space-y-3">
      {/* Stacked bar — 2px gaps between segments */}
      <div
        className="flex h-3 w-full gap-[2px] overflow-hidden rounded"
        role="img"
        aria-label="Verdeling contractuele huur per categorie"
      >
        {visibleSegments.map((category) => (
          <Tooltip key={category.key}>
            <TooltipTrigger asChild>
              <div
                className="h-full first:rounded-l last:rounded-r"
                style={{
                  width: `${percentOf(category.amount, total)}%`,
                  backgroundColor: CATEGORY_COLORS[category.key],
                }}
              />
            </TooltipTrigger>
            <TooltipContent>
              {category.label}: {applyMask(category.amount, "valuta")} (
              {percentOf(category.amount, total).toFixed(1)}%)
            </TooltipContent>
          </Tooltip>
        ))}
      </div>

      {/* Category list — doubles as legend and table view */}
      <div className="divide-y divide-neutral-primary/5">
        {categories.map((category) => {
          const isOpen = expanded === category.key;
          const canExpand = category.lines.length > 0;
          return (
            <div key={category.key}>
              <button
                type="button"
                disabled={!canExpand}
                onClick={() => setExpanded(isOpen ? null : category.key)}
                title={CATEGORY_DESCRIPTIONS[category.key]}
                className="hover:bg-neutral-primary/5 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs disabled:cursor-default disabled:hover:bg-transparent"
              >
                <span className="text-neutral-secondary-mid w-3">
                  {canExpand &&
                    (isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />)}
                </span>
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: CATEGORY_COLORS[category.key] }}
                />
                <span className="text-foreground flex-1 font-medium">
                  {category.label}
                  <span className="text-neutral-secondary-mid ml-1.5 font-normal">
                    {category.contractCount} contracten
                  </span>
                </span>
                <span className="text-neutral-secondary-mid w-12 text-right tabular-nums">
                  {percentOf(category.amount, total).toFixed(1)}%
                </span>
                <span className="text-foreground w-24 text-right font-semibold tabular-nums">
                  {applyMask(category.amount, "valuta")}
                </span>
              </button>

              {isOpen && (
                <div className="mb-1 ml-9 space-y-0.5 pr-2">
                  <p className="text-neutral-secondary-mid pb-1 text-[10px] italic leading-relaxed">
                    {CATEGORY_DESCRIPTIONS[category.key]}
                  </p>
                  {category.lines.map((line) => (
                    <div
                      key={line.key}
                      className="flex items-center gap-2 py-0.5 text-[11px]"
                    >
                      <span className="text-neutral-primary flex-1 truncate">
                        {line.label}
                        <span className="text-neutral-secondary-mid ml-1.5">
                          {line.lineCount}×
                        </span>
                      </span>
                      <span className="text-neutral-primary w-24 text-right tabular-nums">
                        {applyMask(line.amount, "valuta")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
